/**
 * resolveVisibilityView
 *
 * Pure resolver for the visibility view a user should land on when a form of a
 * given type opens (SYD-2080). Resolution order, highest precedence first:
 *
 *   1. Role default — the first `byGroup` rule (in array order) whose group the
 *      user belongs to, provided the rule's view still exists.
 *   2. Form-type default — the form type's `fallback`, if it still exists.
 *   3. System default — `SYSTEM_DEFAULT_VISIBILITY_VIEW`.
 *
 * Candidate views are validated against `availableViewIds` so a configured view
 * that was later deleted is skipped rather than applied. Kept side-effect free
 * so `formStore` can call it inside its subscribe and tests can exercise all
 * three paths directly.
 */

import type { VisibilityTypeId } from '#types/System.types';
import type { FormTypeVisibilityDefault } from '#types/System.Parameters.types';

export const resolveVisibilityView = (input: {
    userGroups: readonly string[];
    defaults: FormTypeVisibilityDefault | undefined;
    availableViewIds: ReadonlySet<VisibilityTypeId>;
    systemDefault: VisibilityTypeId;
}): VisibilityTypeId => {
    const { userGroups, defaults, availableViewIds, systemDefault } = input;

    const groupMatch = defaults?.byGroup?.find(
        (rule) => userGroups.includes(rule.groupId) && availableViewIds.has(rule.view),
    );
    if (groupMatch) return groupMatch.view;

    if (defaults?.fallback !== undefined && availableViewIds.has(defaults.fallback)) {
        return defaults.fallback;
    }

    return systemDefault;
};
